import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useApp } from '../../context';
import { usePlans } from '../../context/plans';
import { useAlert } from '../../hooks/useAlert';
import { BillingCycle, TypeUser, PlanStatus } from '../../types/enums';
import { NavHeader } from '../../components/NavHeader';
import TitlePage from '../../components/TitlePage';
import { Button } from '../../components/Buttons/Button';
import { ModalConfirmPlan } from '../../components/Modals/ModalConfirmPlan';
import { IPlanViewModel } from 'viewModels';
import { PlanController } from '../../controller/PlanController';
import {
  Page,
  Container,
  Cards,
  Card,
  SinglePlanCard,
  Banner,
  WarningBanner,
  DisabledCard,
  DisabledSinglePlanCard,
} from './styles';

export function SubscribePlan() {
  const [redirecting, setRedirecting] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingPaymentError, setPendingPaymentError] = useState<string | null>(null);
  const { user, tokenIsValid } = useAuth();
  const { setLoading } = useApp();
  const alert = useAlert();
  const navigate = useNavigate();
  const { activePlans, loadActivePlans, loading, error, clearError } = usePlans();

  // Função para verificar se o usuário já tem um plano ativo e válido
  const hasValidActivePlan = useMemo(() => {
    if (!user?.userPlan) return false;

    const userPlan = user.userPlan;
    const now = new Date();
    const expirationDate = new Date(userPlan.expirationDate);

    // Verifica se o plano está ativo (status 0) e não expirou
    const isActive = userPlan.status === PlanStatus.ACTIVE;
    const isNotExpired = expirationDate.getTime() > now.getTime();

    return isActive && isNotExpired;
  }, [user?.userPlan]);

  const headerType = useMemo(() => {
    if (!tokenIsValid) return 'loggedOut';
    if (user?.type === TypeUser.COMPANY) return 'company';
    if (user?.type === TypeUser.COLLABORATOR) return 'collaborator';
    return 'user';
  }, [tokenIsValid, user?.type]);

  useEffect(() => {
    if (!tokenIsValid) {
      navigate('/login');
      return;
    }
    loadActivePlans();
  }, [tokenIsValid, navigate, loadActivePlans]);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | undefined>();

  const handleSubscribe = (planId: string) => {
    // Verifica se o usuário já tem um plano ativo e válido
    if (hasValidActivePlan) {
      return; // Não permite contratação se já tem plano válido
    }

    setSelectedPlanId(planId);
    setIsModalOpen(true);
  };

  const selectedPlan = useMemo(
    () => activePlans.find((p) => p.id === selectedPlanId),
    [activePlans, selectedPlanId],
  );

  const handleSubscribePlan = async (plan: IPlanViewModel) => {
    try {
      setSubmitting(true);
      setLoading(true);
      setRedirecting(plan.id);
      setPendingPaymentError(null); // Limpa erro anterior
      const url = await PlanController.startCheckout(plan.id);
      if (url) {
        alert.show({
          type: 'success',
          message: 'Redirecionando para o pagamento...',
        });
        setTimeout(() => {
          window.location.href = url;
        }, 1000);
      }
    } catch (err: any) {
      setRedirecting(null);
      // Verifica se é erro de pagamento pendente
      console.log(err.response.data.message);
      if (err?.response?.data?.message?.includes('pagamento pendente')) {
        setPendingPaymentError(err.response.data.message);
        alert.show({
          type: 'error',
          message: err.response.data.message,
        });
      } else {
        const errorMessage = 'Erro ao processar a contratação do plano. Tente novamente.';
        alert.show({
          type: 'error',
          message: errorMessage,
        });
      }
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const getCycleLabel = (cycle: BillingCycle) =>
    cycle === BillingCycle.MONTHLY ? 'mês' : cycle === BillingCycle.ANNUAL ? 'ano' : '-';

  return (
    <Page>
      <NavHeader typeHeader={headerType as any} />
      <Container>
        <div className="page-title">
          <TitlePage
            title="Planos e assinatura"
            onRequestOpen={() => { }}
            id="start-item"
            iconVisible={false}
          />
        </div>

        {hasValidActivePlan ? (
          <WarningBanner>
            <div className="icon">⚠️</div>
            <div className="content">
              <h3>Você já possui um plano ativo</h3>
              <p>
                Você já possui um plano ativo e válido até{' '}
                {user?.userPlan?.expirationDate
                  ? new Date(user.userPlan.expirationDate).toLocaleDateString('pt-BR')
                  : 'data não informada'}
                . Não é possível contratar um novo plano enquanto o atual estiver ativo.
              </p>
            </div>
          </WarningBanner>
        ) : (
          <Banner>Contrate o plano para seguir acessando o sistema.</Banner>
        )}

        {error && (
          <Banner>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{error}</span>
              <Button
                text="Ok"
                dimensions={{ width: 80, height: 32 }}
                background="tertiary_500"
                textHoverColor="background_white"
                color="background_white"
                backgroundHoverColor="secondary"
                onClick={clearError}
              />
            </div>
          </Banner>
        )}

        {pendingPaymentError && (
          <WarningBanner>
            <div className="icon">⏳</div>
            <div className="content">
              <h3>Pagamento pendente</h3>
              <p>{pendingPaymentError}</p>
              <Button
                text="Entendi"
                dimensions={{ width: 100, height: 32 }}
                background="tertiary_500"
                textHoverColor="background_white"
                color="background_white"
                backgroundHoverColor="secondary"
                onClick={() => setPendingPaymentError(null)}
              />
            </div>
          </WarningBanner>
        )}

        {loading ? (
          <p>Carregando planos...</p>
        ) : activePlans.length === 1 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            {hasValidActivePlan ? (
              <DisabledSinglePlanCard>
                <div>
                  <h2>{activePlans[0].name}</h2>
                  <p className="description">{activePlans[0].description || 'Sem descrição'}</p>
                  <div className="price">
                    <span className="value">R$ {activePlans[0].price.toFixed(2)}</span>
                    <span className="cycle">/ {getCycleLabel(activePlans[0].billingCycle)}</span>
                  </div>
                </div>
                <Button
                  text="Já possui plano ativo"
                  dimensions={{ width: '100%', height: 40 }}
                  background="tertiary_300"
                  color="text_gray"
                  disabled={true}
                />
              </DisabledSinglePlanCard>
            ) : (
              <SinglePlanCard>
                <div>
                  <h2>{activePlans[0].name}</h2>
                  <p className="description">{activePlans[0].description || 'Sem descrição'}</p>
                  <div className="price">
                    <span className="value">R$ {activePlans[0].price.toFixed(2)}</span>
                    <span className="cycle">/ {getCycleLabel(activePlans[0].billingCycle)}</span>
                  </div>
                </div>
                <Button
                  text="Contratar"
                  dimensions={{ width: '100%', height: 40 }}
                  background="tertiary_500"
                  textHoverColor="background_white"
                  color="background_white"
                  backgroundHoverColor="secondary"
                  loading={submitting}
                  onClick={() => handleSubscribe(activePlans[0].id)}
                />
              </SinglePlanCard>
            )}
          </div>
        ) : (
          <Cards>
            {activePlans.map((plan) =>
              hasValidActivePlan ? (
                <DisabledCard key={plan.id}>
                  <div>
                    <h2>{plan.name}</h2>
                    <p className="description">{plan.description || 'Sem descrição'}</p>
                    <div className="price">
                      <span className="value">R$ {plan.price.toFixed(2)}</span>
                      <span className="cycle">/ {getCycleLabel(plan.billingCycle)}</span>
                    </div>
                  </div>
                  <Button
                    text="Já possui plano ativo"
                    dimensions={{ width: '100%', height: 40 }}
                    background="tertiary_300"
                    color="text_gray"
                    disabled={true}
                  />
                </DisabledCard>
              ) : (
                <Card key={plan.id}>
                  <div>
                    <h2>{plan.name}</h2>
                    <p className="description">{plan.description || 'Sem descrição'}</p>
                    <div className="price">
                      <span className="value">R$ {plan.price.toFixed(2)}</span>
                      <span className="cycle">/ {getCycleLabel(plan.billingCycle)}</span>
                    </div>
                  </div>
                  <Button
                    text="Contratar"
                    dimensions={{ width: '100%', height: 40 }}
                    background="tertiary_500"
                    textHoverColor="background_white"
                    color="background_white"
                    backgroundHoverColor="secondary"
                    loading={submitting}
                    onClick={() => handleSubscribe(plan.id)}
                  />
                </Card>
              ),
            )}
          </Cards>
        )}
      </Container>
      {selectedPlan && (
        <ModalConfirmPlan
          isOpen={isModalOpen}
          plan={selectedPlan}
          loading={submitting}
          onConfirm={() => handleSubscribePlan(selectedPlan)}
          onRequestClose={() => {
            setIsModalOpen(false);
            setPendingPaymentError(null);
          }}
          pendingPaymentError={pendingPaymentError}
        />
      )}
    </Page>
  );
}
